import React, { useState, useMemo, useRef } from "react";
import { toast } from "sonner";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { IMG, CONTACT_REASONS } from "@/lib/content";
import { endpoints } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Backdrop } from "@/components/IntersectMotif";

const ORG_TYPES = ["Investor", "VC / PE Firm", "Family Office", "Startup / Founder", "Corporate", "Government / Institution", "Other"];
const INTERESTS = ["Investment", "Venture", "Partnership", "Joint Venture", "Saudi Market Entry", "Other"];

export default function Contact() {
  const { t } = useLang();
  const [form, setForm] = useState({ reason: "", first_name: "", last_name: "", company: "", position: "", email: "", phone: "", country: "", org_type: "", area_of_interest: "", message: "", consent: false });
  const [hp, setHp] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const captcha = useMemo(() => ({ a: 3 + Math.floor(Math.random() * 6), b: 2 + Math.floor(Math.random() * 5) }), []);
  const [answer, setAnswer] = useState("");
  const formRef = useRef(null);
  const [pulse, setPulse] = useState(false);

  const pickReason = (r) => {
    setForm((f) => ({ ...f, reason: r }));
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: window.innerWidth < 1024 ? "start" : "center" });
      setPulse(true);
      setTimeout(() => setPulse(false), 1400);
    }, 90);
  };

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (hp) return;
    if (parseInt(answer, 10) !== captcha.a + captcha.b) { toast.error("Please answer the verification question correctly."); return; }
    if (!form.consent) { toast.error("Please provide consent to continue."); return; }
    setBusy(true);
    try {
      await endpoints.contact(form);
      setDone(true);
      toast.success("Thank you. Your message has been received.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <>
      <PageHero eyebrow="Contact" title="Start a Conversation." image={IMG.boardroom}
        subtitle="Whether you are seeking investment, exploring Saudi Arabia or looking for a partner, we would like to hear from you." />

      <section className="section-pad relative overflow-hidden">
        <Backdrop src={IMG.riyadhNight} opacity={0.12} />
        <div className="container-x relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><p className="eyebrow mb-6">{t("I am…")}</p></Reveal>
            <Reveal delay={0.1}>
              <div className="space-y-1 bg-charcoal/75 backdrop-blur-xl border border-primary-gold/10 p-6 rounded-sm" data-testid="reason-list">
                {CONTACT_REASONS.map((r) => (
                  <button key={r} type="button" onClick={() => pickReason(r)} data-testid={`reason-${r.replace(/\s/g, "-").toLowerCase()}`}
                    className={`block w-full text-left py-3 border-b border-white/5 text-sm transition-colors ${form.reason === r ? "text-primary-gold" : "text-muted-text hover:text-soft-stone"}`}>
                    {t(r)}
                  </button>
                ))}
              </div>
            </Reveal>
          </div>

          <div ref={formRef} className={`lg:col-span-7 lg:col-start-6 rounded-sm transition-all duration-700 ${pulse ? "ring-1 ring-primary-gold/60 shadow-[0_24px_60px_-24px_rgba(198,161,91,0.45)]" : "ring-1 ring-transparent"}`}>
            {done ? (
              <Reveal>
                <div className="bg-charcoal/75 backdrop-blur-xl border border-primary-gold/20 p-10 md:p-12 text-center rounded-sm" data-testid="contact-success">
                  <h3 className="text-2xl font-light text-ivory">{t("Thank you.")}</h3>
                  <p className="mt-4 text-muted-text">{t("Your message has been received. A member of the Intersect team will be in touch.")}</p>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={submit} className="space-y-6 bg-charcoal/75 backdrop-blur-xl border border-primary-gold/15 p-6 md:p-8 rounded-sm" data-testid="contact-form">
                  <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <input required placeholder={t("First Name")} className="field" value={form.first_name} onChange={upd("first_name")} data-testid="input-first-name" />
                    <input required placeholder={t("Last Name")} className="field" value={form.last_name} onChange={upd("last_name")} data-testid="input-last-name" />
                    <input placeholder={t("Company")} className="field" value={form.company} onChange={upd("company")} data-testid="input-company" />
                    <input placeholder={t("Position")} className="field" value={form.position} onChange={upd("position")} data-testid="input-position" />
                    <input required type="email" placeholder={t("Email")} className="field" value={form.email} onChange={upd("email")} data-testid="input-email" />
                    <input placeholder={t("Phone")} className="field" value={form.phone} onChange={upd("phone")} data-testid="input-phone" />
                    <input placeholder={t("Country")} className="field" value={form.country} onChange={upd("country")} data-testid="input-country" />
                    <select className="field text-muted-text" value={form.org_type} onChange={upd("org_type")} data-testid="input-org-type">
                      <option value="" className="bg-obsidian">{t("Organization Type")}</option>
                      {ORG_TYPES.map((o) => <option key={o} value={o} className="bg-obsidian text-ivory">{t(o)}</option>)}
                    </select>
                  </div>
                  <select className="field text-muted-text" value={form.area_of_interest} onChange={upd("area_of_interest")} data-testid="input-interest">
                    <option value="" className="bg-obsidian">{t("Area of Interest")}</option>
                    {INTERESTS.map((o) => <option key={o} value={o} className="bg-obsidian text-ivory">{t(o)}</option>)}
                  </select>
                  <textarea required placeholder={t("Message")} rows={4} className="field resize-none" value={form.message} onChange={upd("message")} data-testid="input-message" />

                  <div className="flex items-center gap-4">
                    <label className="text-sm text-muted-text">{t("Verification: what is")} {captcha.a} + {captcha.b}?</label>
                    <input required className="field w-24" value={answer} onChange={(e) => setAnswer(e.target.value)} data-testid="input-captcha" />
                  </div>

                  <label className="flex items-start gap-3 text-sm text-muted-text cursor-pointer">
                    <input type="checkbox" checked={form.consent} onChange={upd("consent")} className="mt-1 accent-[#C6A15B]" data-testid="input-consent" />
                    <span>{t("I consent to Intersect Holding storing and processing my information in order to respond to my enquiry.")}</span>
                  </label>

                  <button type="submit" disabled={busy} className="btn-solid" data-testid="contact-submit">{busy ? t("Sending…") : t("Start the Conversation")}</button>
                </form>
              </Reveal>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
